import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Folder,
  FileText,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  Copy,
  Check,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const LANG_MAP = {
  js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
  py: 'python', php: 'php', vue: 'vue', json: 'json', md: 'markdown',
  css: 'css', scss: 'css', html: 'html', yml: 'yaml', yaml: 'yaml',
  sh: 'bash', sql: 'sql', env: 'env',
};

function getLanguage(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  return LANG_MAP[ext] || 'text';
}

function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileBrowser({ projectId }) {
  const [items, setItems] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadFiles = useCallback(async (path = '') => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await projectsAPI.files(projectId, path);
      setItems(res.data.items || []);
      setCurrentPath(path);
    } catch (err) {
      if (err.response?.status === 404) {
        setItems([]);
      } else {
        toast.error('Impossible de charger les fichiers');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadFiles('');
    setSelectedFile(null);
    setFileContent('');
  }, [projectId, loadFiles]);

  const openDir = (dirPath) => {
    loadFiles(dirPath);
    setSelectedFile(null);
    setFileContent('');
  };

  const goUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    loadFiles(parts.join('/'));
    setSelectedFile(null);
    setFileContent('');
  };

  const openFile = async (filePath) => {
    setSelectedFile(filePath);
    try {
      const res = await projectsAPI.fileContent(projectId, filePath);
      setFileContent(res.data.content || '');
    } catch {
      setFileContent('// Impossible de charger le fichier');
      toast.error('Impossible de charger le fichier');
    }
  };

  const copyContent = () => {
    navigator.clipboard.writeText(fileContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!projectId) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="flex items-center justify-center h-64 text-muted-foreground">
          Aucun projet sélectionné
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Arborescence */}
      <div className={selectedFile ? 'col-span-4' : 'col-span-12'}>
        <Card className="bg-card border-border h-full">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Folder className="w-4 h-4 text-primary" />
              {currentPath || 'Racine du projet'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => loadFiles(currentPath)} disabled={loading}>
              {loading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {currentPath && (
                <button
                  onClick={goUp}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-accent text-primary transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  ..
                </button>
              )}
              {items.map((item) => (
                <button
                  key={item.path}
                  onClick={() => item.is_dir ? openDir(item.path) : openFile(item.path)}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-accent/50 transition-colors text-left ${
                    selectedFile === item.path ? 'bg-accent text-primary' : 'text-foreground'
                  }`}
                >
                  {item.is_dir ? (
                    <>
                      <ChevronRight className="w-3 h-3 text-muted-foreground" />
                      <Folder className="w-4 h-4 text-amber-500" />
                    </>
                  ) : (
                    <>
                      <span className="w-3" />
                      <FileText className="w-4 h-4 text-muted-foreground" />
                    </>
                  )}
                  <span className="flex-1 truncate">{item.name}</span>
                  {!item.is_dir && item.size != null && (
                    <span className="text-xs text-muted-foreground">{formatSize(item.size)}</span>
                  )}
                </button>
              ))}
              {items.length === 0 && !loading && (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                  Dossier vide
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Contenu du fichier */}
      {selectedFile && (
        <div className="col-span-8">
          <Card className="bg-card border-border h-full">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <CardTitle className="text-sm truncate">{selectedFile}</CardTitle>
                <Badge variant="outline" className="text-xs shrink-0">
                  {getLanguage(selectedFile)}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={copyContent}>
                {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <pre className="p-4 text-xs font-mono leading-relaxed whitespace-pre-wrap bg-muted/30 rounded-b-lg text-foreground">
                  {fileContent || '// Fichier vide'}
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
